import { IProfileResponse } from "@finbackoffice/clientbff-client";
import {
    FC,
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Currency, Language, MNOChannel, UserStatus } from "@finbackoffice/enums";
import { useRouter } from "next/navigation";
import { ClientBFFContext, AuthContext } from "@finbackoffice/site-core";

export interface IUserAccountContextV2 {
    logout: () => Promise<void>;
    updateUser: (payload: Partial<IProfileResponse>) => void;
    loadProfile: () => Promise<void>;
    userCurrency: Currency | "";
    deviceId: string | null;
    userData: IProfileResponse | null;
    userDataLoading: boolean;
    userEmail: IProfileResponse["email"];
    firstName: IProfileResponse["first_name"];
    lastName: IProfileResponse["last_name"];
    username: IProfileResponse["username"];
    userId: number | undefined;
    mno_channel: MNOChannel | undefined;
    userProfileStatus: UserStatus | undefined;
    isUserVerified: boolean;
    userLanguage: Language | undefined;
    userReferralSlug: string;
    userIsAgent: boolean | undefined;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    isKycVerified: boolean;
}

export const UserAccountContextV2 = createContext<IUserAccountContextV2>(null as any);

type IProfileState = {
    data: IProfileResponse | null;
    loading: boolean;
};

type Props = {
    initialProfile: IProfileResponse | null;
    uuid: string;
};

export const UserAccountProviderV2: FC<Props & PropsWithChildren> = ({
    children,
    initialProfile,
    uuid,
}) => {
    const router = useRouter();
    const client = useContext(ClientBFFContext);
    const { setToken, userToken, prevToken } = useContext(AuthContext);
    const [deviceId] = useState<string | null>(uuid);
    const [profile, setProfile] = useState<IProfileState>({
        data: initialProfile,
        loading: false,
    });

    useEffect(() => {
        if (initialProfile) {
            setProfile((state) => ({
                ...state,
                data: initialProfile,
            }));
        } else {
            setProfile((state) => ({
                ...state,
                data: null,
            }));
        }
    }, [initialProfile]);

    useEffect(() => {
        if (userToken && userToken !== prevToken && !profile.data) {
            // for sign up when token reqeusted load profile
            void loadProfile();
        }
        if (prevToken && !userToken && profile.data) {
            void logout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userToken, prevToken, profile.data]);

    const setProfileData = useCallback(
        (val: IProfileResponse) => {
            setProfile((state) => ({
                ...state,
                data: { ...val },
                loading: false,
            }));
        },
        [setProfile],
    );

    const loadProfile = useCallback(async () => {
        setProfile((state) => ({
            ...state,
            loading: true,
        }));
        const response = await client.profile();
        setProfileData(response);
    }, [client, setProfileData]);

    const updateUser = useCallback(
        (
            payload:
                | Partial<IProfileResponse>
                | ((prev: IProfileResponse | null) => Partial<IProfileResponse>),
        ) => {
            setProfile((state) => {
                const updates = typeof payload === "function" ? payload(state.data) : payload;

                return {
                    ...state,
                    data: { ...state.data, ...(updates as IProfileResponse) },
                };
            });
        },
        [],
    );

    const logout = useCallback(async () => {
        await client.logout();
        setToken("");
        setProfile((state) => ({
            ...state,
            data: null,
        }));

        router.refresh();
    }, [client, router, setToken]);

    const userCurrency: Currency | "" = useMemo(
        () => profile.data?.currency || "",
        [profile.data?.currency],
    );

    const value = useMemo(
        () => ({
            logout,
            updateUser,
            loadProfile,
            userCurrency,
            deviceId,
            userData: profile.data,
            userDataLoading: profile.loading,
            userEmail: profile.data?.email || "",
            username: profile.data?.username || "",
            firstName: profile.data?.first_name || "",
            lastName: profile.data?.last_name || "",
            userId: profile.data?.id,
            mno_channel: profile.data?.mno_channel,
            userProfileStatus: profile.data?.status,
            isUserVerified: profile.data?.status === UserStatus.Registered,
            userLanguage: profile.data?.language,
            userReferralSlug: profile.data?.referral_slug || "",
            userIsAgent: profile.data?.is_agent,
            isEmailVerified: profile.data?.email_verified || false,
            isPhoneVerified: profile.data?.phone_verified || false,
            isKycVerified: profile.data?.kyc_id_verified || false,
        }),
        [logout, updateUser, loadProfile, userCurrency, deviceId, profile],
    );

    return <UserAccountContextV2.Provider value={value}>{children}</UserAccountContextV2.Provider>;
};
